import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShoppingCategory } from '@homeapp/shared';

interface Props {
  categories: ShoppingCategory[];
  filter: string | null;
  onFilterChange: (category: string | null) => void;
  onAddCategory: (name: string, icon?: string) => Promise<void>;
}

export default function CategoryFilter({ categories, filter, onFilterChange, onAddCategory }: Props) {
  const { t } = useTranslation();
  const [showAdd, setShowAdd] = useState(false);
  const [newCat, setNewCat] = useState('');

  const handleAddCategory = async () => {
    if (!newCat.trim()) return;
    await onAddCategory(newCat.trim());
    setNewCat('');
    setShowAdd(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <button
          onClick={() => onFilterChange(null)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition ${
            filter === null
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          {t('shopping.filterAll')}
        </button>
        {categories.map(cat => (
          <button
            key={cat.name}
            onClick={() => onFilterChange(filter === cat.name ? null : cat.name)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition ${
              filter === cat.name
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
          >
            {cat.icon} {cat.name}
          </button>
        ))}
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="px-3 py-1.5 rounded-full text-sm bg-gray-200 text-gray-500 hover:bg-gray-300 whitespace-nowrap"
        >
          +
        </button>
      </div>

      {showAdd && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newCat}
            onChange={e => setNewCat(e.target.value)}
            placeholder={t('shopping.categoryName')}
            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            autoFocus
            onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
          />
          <button
            onClick={handleAddCategory}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium"
          >
            {t('shopping.addCategory')}
          </button>
        </div>
      )}
    </div>
  );
}
